/**
 * The user record — `/countries/:cc/admin/users/:userId`. AUTHORED.
 *
 * Three cards, three different write surfaces, one rule joining them (D5):
 *
 *  · ROLES     — `PUT /users/:id/roles`, open to any `users.manage` holder
 *                INSIDE their countries. The editor offers only the caller's
 *                manageable countries; assignments elsewhere render locked
 *                (see `role-editor.tsx`), and the PUT sends ONLY editable-
 *                country entries — the server's partial-replace leaves the
 *                rest untouched, and naming an out-of-scope country would be
 *                refused whole (`COUNTRY_SCOPE_VIOLATION`).
 *  · OVERRIDES — `PUT /users/:id/overrides`, same boundary plus the grant
 *                stops rendered in `overrides-editor.tsx`.
 *  · ACCOUNT   — `PATCH /users/:id` (status, password): `grp:true` ONLY
 *                (D5b, `assertUserRecordMutationAllowed`). The whole card is
 *                ABSENT for anyone else — not disabled, absent: the D5 rule
 *                is that the UI hides what the caller cannot do, and
 *                `users-admin.test.tsx` asserts a non-grp `users.manage`
 *                holder finds neither the disable control nor the password
 *                reset anywhere in the document.
 *
 * PATCH's other members (name/email/phone/is_group_admin edits) are
 * deliberately NOT rendered: the brief's Part B names disable/enable and
 * password reset, and a wider record editor is scope this slice does not
 * take silently. Recorded in the phase report.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserRound } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SkeletonBlock,
  SkeletonRows,
  TextField,
} from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { ConfirmDialog } from './ConfirmDialog';
import { OverridesEditor } from './overrides-editor';
import { RoleAssignmentsEditor } from './role-editor';
import { errorSentence } from './states';
import { RoleChips, StatusChip } from './user-chips';
import { formatLastSignIn } from './user-format';
import {
  useAdminUser,
  useManageableCountries,
  usePutUserOverrides,
  usePutUserRoles,
  useUpdateUser,
  useUserOverrides,
  useUserRoles,
} from './use-admin-users';
import { MIN_PASSWORD_LENGTH } from './users-api';
import type { PermissionOverride, RoleAssignment } from './users-api';

export function UserDetailPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { userId } = useParams<{ userId: string }>();
  const manageable = useManageableCountries();
  const listHref = `/countries/${countryCode}/admin/users`;

  // `users.id` is numeric; anything else cannot name a user, and asking the
  // API to confirm that would be a request spent on a typo.
  const id = userId !== undefined && /^\d{1,15}$/.test(userId) ? userId : null;

  if (manageable.length === 0) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="User management is not available to this account">
          Viewing user records needs the users.manage permission in at least one country.
        </Alert>
      </div>
    );
  }

  if (id === null) {
    return <UserMissing listHref={listHref} />;
  }
  return <UserDetail id={id} listHref={listHref} />;
}

function UserMissing({ listHref }: { listHref: string }): React.JSX.Element {
  return (
    <div className="p-6">
      <EmptyState
        icon={<UserRound aria-hidden="true" className="size-5" />}
        title="This user does not exist"
        description="The link may be stale — the account it pointed at was never created here."
        action={
          <Button variant="ghost" onClick={() => window.history.back()}>
            Go back
          </Button>
        }
      />
      <p className="text-center">
        <Link to={listHref} className="text-label text-indigo underline underline-offset-2">
          All users
        </Link>
      </p>
    </div>
  );
}

function UserDetail({ id, listHref }: { id: string; listHref: string }): React.JSX.Element {
  const navigate = useNavigate();
  const { isGroupAdmin } = useSession();
  const user = useAdminUser(id);

  if (user.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-8 w-64" />
        <SkeletonRows rows={4} label="Loading user…" />
      </div>
    );
  }

  if (user.isError) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <UserMissing listHref={listHref} />
      </div>
    );
  }

  const record = user.data;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title={record.name}
        subtitle={record.email}
        actions={
          <Button variant="ghost" onClick={() => navigate(listHref)}>
            <ArrowLeft aria-hidden="true" className="size-4" />
            Back to users
          </Button>
        }
      />

      <div className="flex max-w-[760px] flex-col gap-4">
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <UserAvatar name={record.name} userId={record.id} size={40} decorative />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-body font-medium text-fg">{record.name}</span>
                <StatusChip status={record.status} />
              </span>
              <RoleChips roles={record.roles} isGroupAdmin={record.is_group_admin} />
            </div>
            <dl className="flex shrink-0 flex-col gap-1 text-label">
              <div className="flex gap-2">
                <dt className="text-fg-secondary">Phone</dt>
                <dd className="text-fg">{record.phone ?? '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-fg-secondary">Last sign-in</dt>
                <dd className="tnum text-fg">{formatLastSignIn(record.last_login_at)}</dd>
              </div>
            </dl>
          </div>
        </Card>

        {/*
          * v22-B (owner ruling) — FOR A GROUP ADMIN THE ROLE AND OVERRIDE
          * FORMS ARE NOT DRAWN AT ALL. Absence, not explanation: this block
          * used to render an info banner ("assignments below still apply to
          * record-keeping but do not limit it") above two fully-enabled forms
          * that changed nothing about what the person can do — a control that
          * lies about its own effect.
          *
          * The record-keeping argument was CHECKED before the forms were
          * removed, and it is empty by the system's own design: `grp:true`
          * carries scope implicitly and writes NO `user_country_roles` rows
          * (spec §10 — seed-admin itself prints "user_country_roles rows=0").
          * There is no record these forms could keep for a group admin; the
          * model refuses to store one. Same principle as the D5b Account card
          * below: absent, never disabled-with-a-caption.
          */}
        {record.is_group_admin ? null : (
          <>
            <RolesCard id={id} />
            <OverridesCard id={id} />
          </>
        )}
        {/* D5b: user-record mutations are grp-only, and the card is ABSENT —
          * not disabled — for everyone else. See the file header. */}
        {isGroupAdmin ? (
          <AccountCard id={id} name={record.name} status={record.status} />
        ) : null}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── roles ── */

function splitByScope<T extends { country: RoleAssignment['country'] }>(
  entries: readonly T[],
  manageable: readonly RoleAssignment['country'][],
): { editable: T[]; locked: T[] } {
  const editable: T[] = [];
  const locked: T[] = [];
  for (const entry of entries) {
    (manageable.includes(entry.country) ? editable : locked).push(entry);
  }
  return { editable, locked };
}

function RolesCard({ id }: { id: string }): React.JSX.Element {
  const manageable = useManageableCountries();
  const roles = useUserRoles(id);
  const save = usePutUserRoles(id);

  const server = roles.data === undefined ? null : splitByScope(roles.data.assignments, manageable);
  const serverKey = JSON.stringify(server?.editable ?? null);

  const [draft, setDraft] = useState<RoleAssignment[] | null>(null);
  const [mirror, setMirror] = useState(serverKey);
  // "Adjust state when the data changes" (the SearchField pattern): a save —
  // or another admin's save landing via invalidation — resets the draft to
  // what the server now holds.
  if (mirror !== serverKey) {
    setMirror(serverKey);
    setDraft(null);
  }

  const value = draft ?? server?.editable ?? [];
  const dirty = draft !== null && JSON.stringify(draft) !== serverKey;

  return (
    <Card
      title="Country roles"
      action={
        <Button
          onClick={() => save.mutate(value)}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save roles'}
        </Button>
      }
    >
      {roles.isPending ? (
        <SkeletonRows rows={2} label="Loading roles…" />
      ) : roles.isError ? (
        <Alert tone="danger" title="Roles could not be loaded">
          Reload to try again. Nothing was changed.
        </Alert>
      ) : (
        <>
          {save.isError ? (
            <Alert tone="danger" title="The roles were not saved">
              {errorSentence(save.error, 'The server refused the change. Nothing was saved.')}
            </Alert>
          ) : null}
          <RoleAssignmentsEditor
            value={value}
            onChange={setDraft}
            countries={manageable}
            lockedAssignments={server?.locked ?? []}
          />
        </>
      )}
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────── overrides ── */

function OverridesCard({ id }: { id: string }): React.JSX.Element {
  const manageable = useManageableCountries();
  const overrides = useUserOverrides(id);
  const save = usePutUserOverrides(id);

  const server =
    overrides.data === undefined ? null : splitByScope(overrides.data.overrides, manageable);
  const serverKey = JSON.stringify(server?.editable ?? null);

  const [draft, setDraft] = useState<PermissionOverride[] | null>(null);
  const [mirror, setMirror] = useState(serverKey);
  if (mirror !== serverKey) {
    setMirror(serverKey);
    setDraft(null);
  }

  const value = draft ?? server?.editable ?? [];
  const dirty = draft !== null && JSON.stringify(draft) !== serverKey;

  return (
    <Card
      title="Permission overrides"
      action={
        <Button onClick={() => save.mutate(value)} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save overrides'}
        </Button>
      }
    >
      {overrides.isPending ? (
        <SkeletonRows rows={2} label="Loading overrides…" />
      ) : overrides.isError ? (
        <Alert tone="danger" title="Overrides could not be loaded">
          Reload to try again. Nothing was changed.
        </Alert>
      ) : (
        <>
          {save.isError ? (
            <Alert tone="danger" title="The overrides were not saved">
              {errorSentence(save.error, 'The server refused the change. Nothing was saved.')}
            </Alert>
          ) : null}
          <OverridesEditor
            value={value}
            onChange={setDraft}
            countries={manageable}
            lockedOverrides={server?.locked ?? []}
          />
        </>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────────── account actions (grp) ── */

function AccountCard({
  id,
  name,
  status,
}: {
  id: string;
  name: string;
  status: 'active' | 'disabled';
}): React.JSX.Element {
  const update = useUpdateUser(id);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  const toggleStatus = (): void => {
    update.mutate(
      { status: status === 'active' ? 'disabled' : 'active' },
      { onSuccess: () => setConfirmingDisable(false) },
    );
  };

  const resetPassword = (): void => {
    if (password.length < MIN_PASSWORD_LENGTH || update.isPending) return;
    update.mutate(
      { password },
      {
        onSuccess: () => {
          setPassword('');
          setPasswordSaved(true);
        },
      },
    );
  };

  return (
    <Card title="Account">
      {update.isError ? (
        <Alert tone="danger" title="The change was not saved">
          {errorSentence(update.error, 'The server refused the change. Nothing was saved.')}
        </Alert>
      ) : null}

      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <p className="text-body font-medium text-fg">
              {status === 'active' ? 'Disable this account' : 'Enable this account'}
            </p>
            <p className="text-label text-fg-secondary">
              {status === 'active'
                ? 'They will not be able to sign in until it is enabled again. Nothing is deleted.'
                : 'They will be able to sign in again with their existing password.'}
            </p>
          </div>
          {status === 'active' ? (
            /* A NEUTRAL ghost trigger, deliberately: red text on the surface
             * (text-danger-fg) measures 2.98:1 in dark mode. The danger weight
             * lives in the ConfirmDialog's filled confirm button, whose
             * white-on-#ca2525 pair is mode-invariant at 5.52:1. */
            <Button
              variant="ghost"
              onClick={() => setConfirmingDisable(true)}
              disabled={update.isPending}
            >
              Disable account
            </Button>
          ) : (
            <Button onClick={toggleStatus} disabled={update.isPending}>
              {update.isPending ? 'Working…' : 'Enable account'}
            </Button>
          )}
        </div>

        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            resetPassword();
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-full max-w-[320px]">
              <TextField
                label={`New password (at least ${String(MIN_PASSWORD_LENGTH)} characters)`}
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordSaved(false);
                }}
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              variant="ghost"
              disabled={password.length < MIN_PASSWORD_LENGTH || update.isPending}
            >
              {update.isPending ? 'Working…' : 'Reset password'}
            </Button>
          </div>
          {passwordSaved ? (
            /* On the success TINT (4.55:1 both modes), not bare text-ok-fg on
             * the surface — that pair measures 3.23:1 in dark mode. Same rule
             * as the create form's refusal: status foregrounds sit on their
             * own tint. */
            <p
              role="status"
              className="w-fit rounded-sm bg-ok-bg px-3 py-2 text-label text-ok-fg"
            >
              Password changed. Their existing sessions stay signed in until they expire.
            </p>
          ) : null}
        </form>
      </div>

      <ConfirmDialog
        open={confirmingDisable}
        title={`Disable ${name}’s account?`}
        body="They will be signed out of nothing immediately, but every new sign-in will be refused until the account is enabled again. Nothing is deleted."
        confirmLabel="Disable account"
        busy={update.isPending}
        onConfirm={toggleStatus}
        onCancel={() => setConfirmingDisable(false)}
      />
    </Card>
  );
}
